import { useMemo } from 'react';
import { useI18nContext } from '@/i18n/i18n-react';
import type { Locales } from '@/i18n/i18n-types';
import { WithTooltip } from '@/components/with-tooltip';
import { buildAudienceCompositionSegments } from '@/components/audiences/build-audience-composition-segments';
import { Cell, Pie, PieChart } from 'recharts';
import { cn } from '@/lib/utils';

const CHART_SIZE = 60;

interface AudienceListItemDonutProps {
  targetUsersCount: number;
  totalUserbaseCount: number;
  locale: Locales;
}

function getCenterFontSize(formattedCount: string) {
  const length = formattedCount.length;
  if (length <= 4) return 'text-sm';
  if (length <= 6) return 'text-xs';
  return 'text-[11px]';
}

export function AudienceListItemDonut({
  targetUsersCount,
  totalUserbaseCount,
  locale,
}: AudienceListItemDonutProps) {
  const { LL } = useI18nContext();

  const data = useMemo(
    () =>
      buildAudienceCompositionSegments({
        targetUsersCount,
        totalUserbaseCount,
        labels: {
          usersInAudience: LL.audiences.details.overview.usersInAudience(),
          otherUsers: LL.audiences.details.overview.otherUsers(),
        },
        zeroTotalSliceColor: 'other',
      }),
    [targetUsersCount, totalUserbaseCount, LL.audiences.details.overview]
  );

  const formattedCount = targetUsersCount.toLocaleString(locale);
  const tooltip = LL.audiences.listMetrics.compositionTooltip({
    target: formattedCount,
    total: totalUserbaseCount.toLocaleString(locale),
  });

  return (
    <WithTooltip tooltip={tooltip}>
      <div className="relative size-[60px] shrink-0">
        <PieChart width={CHART_SIZE} height={CHART_SIZE}>
          <Pie
            data={data}
            dataKey="value"
            nameKey="label"
            cx="50%"
            cy="50%"
            innerRadius="85%"
            outerRadius="100%"
            startAngle={90}
            endAngle={-270}
            paddingAngle={data.length > 1 ? 3 : 0}
            strokeWidth={0}
            isAnimationActive={false}
            tabIndex={-1}
            style={{ outline: 'none' }}
          >
            {data.map((entry) => (
              <Cell
                key={entry.key}
                fill={entry.color}
                tabIndex={-1}
                style={{ outline: 'none' }}
              />
            ))}
          </Pie>
        </PieChart>
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span
            className={cn(
              'font-semibold tabular-nums tracking-tight text-foreground',
              getCenterFontSize(formattedCount)
            )}
          >
            {formattedCount}
          </span>
        </div>
      </div>
    </WithTooltip>
  );
}
